(ns quuppa.peweb.peinfo-locators
  (:require [clojure.string :as s]
            [goog.string :as gstring]
            [quuppa.peweb.peinfo-utils :as peinfo-utils]
            [quuppa.utils :as quuppa-utils]
            [goog.string.format]
            [linked.core :as l]
            [komponentit.timeago :as timeago]
            [malli.util :as mu]
            [quuppa.ui.button :as button]
            [quuppa.ui.tooltip :as tooltip]
            [re-frame.core :as rf :refer [reg-sub subscribe]]
            [quuppa.date-time.core :as date-time]
            [quuppa.pe-domain :as pe-domain]
            [quuppa.peweb.tables :as tables]
            [quuppa.peweb.peinfo-events :as e]
            [quuppa.ui.layout :as layout]
            [quuppa.ui.icons :refer [icon]]
            [quuppa.ui.alert-popover :refer [alert-popover]]
            [quuppa.utils :as utils]
            [quuppa.peweb.tanstack-table :as tanstack-table]
            [reagent.core :as r]))

(defn time->color-class
  "Coloring class for time value. Like `getColor` in PE core."
  [time-value]
  (cond
    (< time-value 0) "bg-warning"
    (< time-value 5000) "bg-success"
    (< time-value 60000) "bg-warning"
    :else "bg-danger"))

(defn last-packet->display-value [value]
  (if (vector? value)
    ;; Legacy value
    (let [[ago delta] value]
      (when ago
        [:span.p-1
         {:class (time->color-class delta)}
         ago]))
    (when value
      [timeago/timeago-2
       {:value (date-time/temporal->native-date value)
        :format-fn (fn [{:keys [diff-ms] :as x}]
                     [:span
                      {:class (time->color-class diff-ms)}
                      (date-time/duration-string x)])}])))

(defn conn-status->class [connection-status-enum]
  (case connection-status-enum
    :Good "bg-primary"
    :Medium "bg-warning"
    (:SetupError :Bad) "bg-danger"
    :NoConnection "bg-light"
    nil))

(defn command-status-name [k]
  (case k
    :WaitingForStatus "Waiting"
    :WaitingForReply "Ok"
    :Ok "Ok"
    nil nil
    (name k)))

(defn render-number [n]
  (when n
    (gstring/format "%.2f" n)))

(defn render-degrees
  "If we don't have value (nil) return nil and the table render will add empty marker.
  If we have value that says we don't know the value, render warning.
  Else render the value as degrees"
  [n]
  (cond
    (nil? n) nil

    ;; TODO: JS MIN_VALUE is the smallest representable POSITIVE number, i.e. as close to zero
    ;; The value from PE core is Java max negative integer probably.
    ;; We should handle these values in the backend in some environment agnostic way.
    ;; E.g. :locator-info :azimuth-rad already checks for Double/MIN_VALUE in the QPE-next
    ;; and returns nil, but other fields probably don't have the same check.

    ; (<= n js/Number.MIN_VALUE) "Unknown"
    :else (render-number (utils/radians->degrees n))))

(def locator-sidebar-tabs
  [{:id "layout" :label "Layout"}
   {:id "radio" :label "Radio"}
   {:id "wireless" :label "Wireless"}
   {:id "network" :label "Network"}])

(def default-empty-value-unknown
  [:hw-type :hw-type-detected :hw-type-planned :uptime :acc-az-corr :acc-tilt-ele :acc-az-corr-error :acc-tilt-ele-error :sensitivity
   :identification-status :command-delay :df-last-packet :last-good-packet :data-last-packet :sync-per-second
   :deployment-mode :subscription :commanded-receive-channels :main-version :radio-version :com-radio-version
   :ant-switch-version :wireless-type :wireless-rssi :ip-address :ethernet-mac :qxx-license-data :local-subscription-status])

(def default-empty-value-not-set
  [:name :cs :area :mode :override-script :x :y :z])

(def default-empty-value-none
  [:wireless-hub :wireless-satellites])

(defn empty-cell-placeholder [column-key]
  (cond (some #{column-key} default-empty-value-unknown)
        "Unknown"
        (some #{column-key} default-empty-value-not-set)
        "NotSet"
        (some #{column-key} default-empty-value-none)
        "None"
        :else
        "Unknown"))

(defn sorting-with-empty-placeholder-values [row-a row-b column-id]
  (let [a (.getValue row-a column-id)
        b (.getValue row-b column-id)
        empty-a? (peinfo-utils/safe-empty? a)
        empty-b? (peinfo-utils/safe-empty? b)]
    (cond
      (and empty-a? empty-b?) 0
      empty-a? 1   ; empty values go last
      empty-b? -1  ; empty values go last
      (and (number? a) (number? b)) (compare a b)
      (number? a) -1  ; numbers before strings
      (number? b) 1
      :else (compare (s/lower-case (str a)) (s/lower-case (str b))))))

(defn sorting-numbers-with-placeholder-last [row-a row-b column-id]
  (let [a (.getValue row-a column-id)
        b (.getValue row-b column-id)
        nil-or-placeholder? #(or (nil? %)
                                 (= % (empty-cell-placeholder column-id)))]
    (cond
      (and (nil-or-placeholder? a) (nil-or-placeholder? b)) 0
      (nil-or-placeholder? a) 1
      (nil-or-placeholder? b) -1
      (and (number? a) (number? b)) (compare a b)
      :else (compare (str a) (str b)))))

(defn sorting-strings-case-insensitive [row-a row-b column-id]
  (let [a (.getValue row-a column-id)
        b (.getValue row-b column-id)
        empty-a? (peinfo-utils/safe-empty? a)
        empty-b? (peinfo-utils/safe-empty? b)]
    (cond
      (and empty-a? empty-b?) 0
      empty-a? 1
      empty-b? -1
      :else (compare (s/lower-case (str a)) (s/lower-case (str b))))))

(defn temporal-sorting-fn [row-a row-b column-id]
  (let [a (.getValue row-a column-id)
        b (.getValue row-b column-id)]
    (cond
      (and (nil? a) (nil? b)) 0
      (nil? a) 1
      (nil? b) -1
      :else (.compareTo a b))))

(defn sorting-mixed-numbers-and-strings [row-a row-b column-id]
  (let [a (.getValue row-a column-id)
        b (.getValue row-b column-id)
        empty-a? (peinfo-utils/safe-empty? a)
        empty-b? (peinfo-utils/safe-empty? b)]
    (cond
      (and empty-a? empty-b?) 0
      empty-a? 1
      empty-b? -1
      (and (number? a) (number? b)) (compare a b)
      (number? a) -1
      (number? b) 1
      :else (compare (s/lower-case (str a)) (s/lower-case (str b))))))

(def locator-columns-main
  (l/map
    :name
    {:value-fn :name
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :name)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :width 150
     :label "Name"}

    :id
    {:value-fn :id
     :render-fn (fn [value row-data _]
                  (if (:has-dummy-id row-data)
                    [:i "Not identified"]
                    value))
     :sortingFn sorting-strings-case-insensitive
     :sortable true
     :width 150
     :label "ID"}

    :cs
    {:value-fn :coordinate-system-name
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :cs)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :width    150
     :label    "Coordinate system"}

    :area
    {:value-fn (fn [locator]
                 (let [grids (->> (or (:trackingareas locator)
                                      ;; TODO: Legacy name, can be removed later.
                                      (:grids locator))
                                  (map :name-id))]
                   (if (empty? grids)
                     "*noarea*"
                     (s/join "," grids))))
     :class-fn (fn [value _ _]
                 (if (= value "*noarea*")
                   "bg-warning"
                   nil))
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :area)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :width    200
     :label    "Area"}

    :connection
    {:value-fn (fn [l]
                 (let [fw-too-old? (get-in l [:locator-info :fw-too-old?])]
                   (if fw-too-old?
                     (str (or (:connection-status l) (:command-status l)) ", too old FW")
                     (or (:connection-status l) (:command-status l)))))
     :render-fn command-status-name
     :class-fn (fn [value locator _props]
                 (if (-> locator :locator-info :fw-too-old?)
                   "bg-danger"
                   (case value
                     :Discovered "bg-light"
                     :Failed "bg-danger"
                     :Ok "bg-success"
                     (:Waiting :FWUpgrade :NotConnectable) "bg-warning"
                     ;; sc-33730 old QPE versions do not send connection-status, but 'locator command status'.
                     ;; bringing back the legacy values to UI, as it is now blank if :connection-status not present.
                     :WaitingForReply "bg-success"
                     :WaitingForStatus "bg-warning"
                     nil)))
     :sortingFn sorting-strings-case-insensitive
     :sortable true
     :width    150
     :label    "Connection"}

     :hw-type
    {:value-fn :hw-type
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :hw-type)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :width    150
     :label    "Type"}))

(def locator-columns-hw-type-split
  (l/map
    :hw-type-detected
    {:value-fn  :hw-type-detected
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :hw-type-detected)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable  true
     :width     150
     :label     "Detected Type"}

    :hw-type-planned
    {:value-fn  :hw-type-planned
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :hw-type-planned)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable  true
     :width     150
     :label     "Planned Type"}))

(defn format-location [k v]
  (when-let [x (get-in v [:converted-location k])]
    (gstring/format "%.2f" x)))

(def locator-columns-layout
  (l/map
    :uptime
    {:value-fn  #(get-in % [:locator-info :last-reset-time])
     :render-fn (fn [t]
                  (if (nil? t)
                    (empty-cell-placeholder :uptime)
                    ;; Legacy value
                    (if (number? t)
                      t
                      (date-time/format-instant t))))
     :sortingFn temporal-sorting-fn
     :sortable true
     :label    "Previous reset"}

    :mode
    {:value-fn (fn [locator]
                 (if (some #{(:mode locator)} ["Normal" :Normal])
                   "Track"
                   (:mode locator)))
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :mode)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :label    "Mode"}

    :acc-az-corr
    {;; Matches classic probably?
     ;; :value-fn #(get-in % [:locator-info :accelerometer-data :az-corr])
     ;; sc-27068: alignment orientation value set in QMD/QSP
     :value-fn #(get-in % [:orientation :azimuth-rad])
     :render-fn (fn [value]
                  (if (nil? value)
                    (empty-cell-placeholder :acc-az-corr)
                    (render-degrees value)))
     :sortingFn sorting-numbers-with-placeholder-last
     :sortable true
     :label    "Orientation, rotation (deg)"}

    :acc-tilt-ele
    {:value-fn #(get-in % [:locator-info :accelerometer-data :tilt-ele])
     :render-fn (fn [value]
                  (if (nil? value)
                    (empty-cell-placeholder :acc-tilt-ele)
                    (render-degrees value)))
     :sortingFn sorting-numbers-with-placeholder-last
     :sortable true
     :label    "Orientation, elevation (deg)"}

    :acc-az-corr-error
    {:value-fn (fn [l]
                 (let [az-corr (get-in l [:locator-info :accelerometer-data :az-corr])
                       azimuth-rad (get-in l [:orientation :azimuth-rad])
                       tilt-elevation-rad (get-in l [:orientation :tilt-elevation-rad])
                       limit-rad (utils/degrees->radians 10)]
                   (cond
                     ; (<= az-corr js/Number.MIN_VALUE) nil

                     (and azimuth-rad
                          (> tilt-elevation-rad limit-rad)
                          (> (- Math/PI azimuth-rad) limit-rad))
                     (Math/abs (utils/mod-pi (utils/radians->degrees (- az-corr azimuth-rad)))))))
     :sortable true
     :sortingFn sorting-numbers-with-placeholder-last
     :render-fn (fn [value]
                  (if (nil? value)
                    (empty-cell-placeholder :acc-az-corr-error)
                    (render-degrees value)))
     :label    "Orientation error, rotation (deg)"}

    :acc-tilt-ele-error
    {:value-fn (fn [l]
                 (let [tilt-ele (get-in l [:locator-info :accelerometer-data :tilt-ele])
                       tilt-elevation-rad (get-in l [:orientation :tilt-elevation-rad])]
                   (cond
                     ; (<= tilt-ele js/Number.MIN_VALUE) nil
                     :else (when tilt-elevation-rad
                             (Math/abs (- tilt-ele tilt-elevation-rad))))))
     :render-fn (fn [value]
                  (if (nil? value)
                    (empty-cell-placeholder :acc-tilt-ele-error)
                    (render-degrees value)))
     :sortingFn sorting-numbers-with-placeholder-last
     :sortable true
     :label    "Orientation error, elevation (deg)"}

    :override-script
    {:value-fn #(case (:script-source %)
                  "Override" (:raw-script-id %)
                  (empty-cell-placeholder :override-script))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :label    "Startup override"}

    :scan-mode
    {; FIXME implement full support for showing override
     ; FIXME show warning color isn't in the mode the tagcommander thinks it should be
     ;if (locator.getScanMode() == TagCommander.ins().getCurrentScanMode(locator)) return okColor;
     :value-fn :scan-mode
     :sortingFn sorting-strings-case-insensitive
     :sortable true
     :label    "Scan mode"}

    :sensitivity
    {:value-fn :fixed-sensitivity
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty-without-number-check? value)
                    (empty-cell-placeholder :sensitivity)
                    (render-number value)))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :label    "Sensitivity (dB)"}

    :x
    {:value-fn #(get-in % [:converted-location :x])
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty-without-number-check? value)
                    (empty-cell-placeholder :x)
                    (render-number value)))
     :sortingFn sorting-numbers-with-placeholder-last
     :sortable true
     :label    "x (m)"}

    :y
    {:value-fn #(get-in % [:converted-location :y])
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty-without-number-check? value)
                    (empty-cell-placeholder :y)
                    (render-number value)))
     :sortingFn sorting-numbers-with-placeholder-last
     :sortable true
     :label    "y (m)"}

    :z
    {:value-fn #(get-in % [:converted-location :z])
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty-without-number-check? value)
                    (empty-cell-placeholder :z)
                    (render-number value)))
     :sortingFn sorting-numbers-with-placeholder-last
     :sortable true
     :label    "z (m)"}

    :identification-status
    {:value-fn pe-domain/locator->identification-status
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :identification-status)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :label    "Identification"}

    :focusing-status
    {:value-fn pe-domain/locator->focusing-status
     :sortable true
     :sortingFn sorting-strings-case-insensitive
     :label    "Focusing"}

    :command-status
    {:value-fn :command-status
     :render-fn command-status-name
     :class-fn (fn [s _ _]
                 (case s
                   (:WaitingForReply :Ok) "bg-success"
                   :WaitingForStatus "bg-warning"
                   nil))
     :sortingFn sorting-strings-case-insensitive
     :sortable true
     :width 150
     :label "Command status"}

    :command-delay
    {:value-fn #(let [c-delay (:command-delay %)]
                  (if (> c-delay 0)
                    c-delay
                    (empty-cell-placeholder :command-delay)))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :label    "Command delay (ms)"}))

(defn firmware-version
  [firmware-key value {:keys [qpe-subscribe]}]
  (let [latest @(qpe-subscribe [:qpe/data :controller-state :firmware-versions firmware-key])]
    (if value
      [:span.w-100.d-inline-flex.flex-row.align-items-center
       [:span.flex-grow-1
        value
        (when (not= latest value)
          [:span
           " (Latest: " latest ")"])]]
      "-")))

(defn parse-version [v]
  (mapv #(quuppa-utils/parse-int %) (s/split v #"\.")))

(defn compare-versions [a b]
  (compare (parse-version a) (parse-version b)))

(defn firmware-class
  [firmware-key value _locator {:keys [qpe-subscribe]}]
  (let [latest @(qpe-subscribe [:qpe/data :controller-state :firmware-versions firmware-key])]
    (when value
      (if (or (= latest value)
              (> (compare-versions value latest) 0))
        "bg-success"
        "bg-warning"))))

(def locator-columns-radio
  (l/map
    :df-last-packet
    {:value-fn #(get-in % [:df-packet-statistics :last-time])
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :df-last-packet)
                    (last-packet->display-value value)))
     :sortingFn temporal-sorting-fn
     :sortable true
     :label    "Last DF packet"}

    :last-good-packet
    {:value-fn #(get-in % [:good-packet-statistics :last-time])
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :last-good-packet)
                    (last-packet->display-value value)))
     :sortingFn temporal-sorting-fn
     :sortable true
     :label    "Last Good DF"}

    :data-last-packet
    {:value-fn #(get-in % [:data-packet-statistics :last-time])
     :sortingFn temporal-sorting-fn
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :data-last-packet)
                    (last-packet->display-value value)))
     :sortable true
     :label    "Last Data Packet"}

    :df-pps
    {:value-fn #(get-in % [:df-packet-statistics :short-pps])
     :render-fn render-number
     :sortable true
     :label    "DF pps"}

    :data-pps
    {:value-fn #(get-in % [:data-packet-statistics :short-pps])
     :render-fn render-number
     :sortable true
     :label    "Data pps"}

    :sync-per-second
    {:value-fn (fn [l]
                 (let [[value _time] (:sync-per-second l)]
                   (cond
                     (= (:mode l) "Deployment")
                     "deployment"

                     :else
                     (render-number value))))
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :sync-per-second)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     ;; "Sync/s (age)", current the age part isn't displayed
     :sortable true
     :label    "Sync/s"}


    :networkloss-pps
    {:value-fn (fn [locator]
                 (let [active-qpe @(subscribe [:qpe/active-qpe])
                       ;;server-info @(qpe-subscribe [:qpe/server-info])
                       ;;controller-state @(qpe-subscribe [:qpe/controller-state])
                       system-status @(subscribe [:qpe/system-status active-qpe])]
                   (cond
                     ; we should also check for TagCommander.ins().getCurrentScanMode( but roll this into mode
                     (= (:mode locator) "Deployment") "Deployment"
                     (> (get-in system-status [:turbo-packet-statistics :short-pps]) 0.1) "Turbo mode"
                     :else (render-number (get-in locator [:dropped-after-locator-packet-statistics :short-pps])))))
     :sortingFn sorting-mixed-numbers-and-strings
     :sortable true
     :label    "Network loss pps"}

    :deployment-mode
    {:value-fn (fn [locator]
                 (let [active-script (:active-script locator)
                       active-qpe @(subscribe [:qpe/active-qpe])
                       server-info @(subscribe [:qpe/server-info active-qpe])
                       controller-state @(subscribe [:qpe/controller-state active-qpe])]
                   (cond
                     (and (:use-failover server-info) (not (:failover-active? server-info)))
                     "Failover not active"
                     (= (:target-mode controller-state) :Deployment) "QPE deployment"
                     (= (:mode locator) "Deployment") "Deactivate"
                     (= (:mode locator) "Moved") "Moved, Fix"
                     (and (nil? active-script) (not (:use-failover server-info))) "NotReady"
                     :else "Activate")))
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :deployment-mode)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :label    "Deployment mode"}

    :qxx-license-data
    {:value-fn (fn [locator]
                 (let [active-qpe       @(subscribe [:qpe/active-qpe])
                       controller-state @(subscribe [:qpe/controller-state active-qpe])
                       license          (-> (get locator :qxx-license-data)
                                            (assoc :deployment? (:deployment-mode? controller-state)))]
                   (cond
                     (:deployment? license) "deployment"
                     (= (:locator-generation locator) "Qxx")
                     (if (false? (:pk-crypter? license))
                       "No key"
                       (if (keyword? (:status license))
                         (name (:status license))
                         (str (:status license))))
                     :else (empty-cell-placeholder :qxx-license-data))))

     :render-fn (fn [value _locator _props] value)
     :class-fn (fn [{license-status :status :as license} _locator _props]
                 (let [expiring-class (when-let [days (some-> (when (pos? (:time-to-expire license-status))
                                                                (:time-to-expire license-status))
                                                              (/ (* 1000 3600 24)))]
                                        (cond
                                          (< days 0.5) "bg-error"
                                          (< days 1.0) "bg-warning"))]
                   (cond
                     (false? (:pk-crypter? license)) "bg-warning"
                     (true? (:atecc-failure license)) "bg-warning"
                     (some? expiring-class) expiring-class
                     :else (case license-status
                             (:Expired :Invalid :PETooNew :NotYetActive :PETimeInPast) "bg-error"
                             (:Lite :Ok) "bg-success"
                             nil))))
     :sortingFn (fn [row-a row-b column-id]
                  (let [a (.getValue row-a column-id)
                        b (.getValue row-b column-id)
                        placeholder? #(or (nil? %) (= % (empty-cell-placeholder column-id)))]
                    (cond
                      (and (placeholder? a) (placeholder? b)) 0
                      (placeholder? a) 1
                      (placeholder? b) -1
                      :else (compare (s/lower-case (str a)) (s/lower-case (str b))))))
     :sortable true
     :label "Licensing Data"}

    :subscription
    {:value-fn  :subscription
     :render-fn (fn [{:keys [id type days-left perpetual? offline-enabled wireless-enabled version-lock]} _locator _props]
                  (let [days-left        (cond
                                           perpetual?         ", perpetual"
                                           (number? days-left) (str ", " (int (Math/floor days-left)) " days left")
                                           (string? days-left) (str ", " days-left))
                        offline-enabled  (when offline-enabled ", offline")
                        wireless-enabled (when wireless-enabled ", Q-Link")
                        version-lock     (when version-lock (str ", max QPE version " version-lock))]
                    (if (and (nil? type) (nil? id) (nil? days-left))
                      (empty-cell-placeholder :subscription)
                      [:span (str id ", " type days-left offline-enabled wireless-enabled version-lock)])))
     :class-fn (fn [{:keys [days-left-numeric too-new-pe is-expired?] :as sub}]
                 (cond (nil? sub)
                       ""
                       (and days-left-numeric (< days-left-numeric 14))
                       "br-warning"
                       (or too-new-pe is-expired?)
                       "bg-danger"
                       :else
                       "bg-success"))
     :sortingFn sorting-with-empty-placeholder-values
     :label     "Subscription"}

    :local-subscription-status
    {:value-fn :local-subscription-status
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :local-subscription-status)
                    value))
     :class-fn (fn [_value {:keys [has-dummy-id qxx-license-data authenticating-sub? qam-file-status]}
                    {:keys [qpe-subscribe]}]
                 (let [file-sync-status @(qpe-subscribe [:qpe/data :controller-state :file-sync-status])]
                   (cond
                     has-dummy-id
                     "bg-warning"
                     (:license-update-started? qxx-license-data)
                     "bg-warning"
                     authenticating-sub?
                     (cond
                       (= :Running file-sync-status) "bg-warning"
                       (= :Error file-sync-status) "bg-danger"
                       (not= :Scheduled file-sync-status)
                       (case qam-file-status
                        :Failed        "bg-danger"
                        :Unknown       "bg-warning"
                        :WritingToFile "bg-warning"
                        :Written       "bg-success"
                        nil)
                       :else ""))))
     :label "Local subscription status"}

    :commanded-receive-channels
    {:value-fn #(:receive-channel-str %)
     :render-fn (fn [value]
                  (if (nil? value)
                    (empty-cell-placeholder :commanded-receive-channels)
                    value))
     :sortable true
     :label    "Receive channels (MHz/BLE)"}

    :main-version
    {:value-fn  #(get-in % [:locator-info :main-fw-str])
     :render-fn (fn [value locator props]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :main-version)
                    (firmware-version :latestQxxMainFW value props)))
     :sortingFn sorting-with-empty-placeholder-values
     :class-fn  (partial firmware-class :latestQxxMainFW)
     :sortable  true
     :label     "Main FW"}

    :radio-version
    {:value-fn  #(get-in % [:locator-info :pos-radio-fw-str])
     :render-fn (fn [value locator props]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :radio-version)
                    (firmware-version :latestQxxPositioningRadioFW value props)))
     :sortingFn sorting-with-empty-placeholder-values
     :class-fn  (partial firmware-class :latestQxxPositioningRadioFW)
     :sortable  true
     :label     "Pos Radio FW"}

    :com-radio-version
    {:value-fn  #(get-in % [:locator-info :com-radio-fw-str])
     :render-fn (fn [value locator props]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :com-radio-version)
                    (firmware-version :latestQxxCommunicationRadioFW value props)))
     :class-fn  (partial firmware-class :latestQxxCommunicationRadioFW)
     :sortingFn sorting-with-empty-placeholder-values
     :sortable  true
     :label     "Com Radio FW"}

    :ant-switch-version
    {:value-fn #(get-in % [:locator-info :antenna-switcher-fw-str])
     :render-fn (fn [value locator props]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :ant-switch-version)
                    (firmware-version :latestQxxAntennaSwitcherFW value props)))
     :class-fn (partial firmware-class :latestQxxAntennaSwitcherFW)
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :label    "Ant switch FW"}))

(def locator-columns-wireless
  (l/map
    :wireless-type
    {:value-fn (comp :type :wireless)
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :wireless-type)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     ;; TODO: colors
     :label "Q-Link type"
     :sortable true}

    :wireless-status
    {:value-fn (comp :connection-status :wireless)
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    "NoConnection"
                    value))
     :sortingFn sorting-strings-case-insensitive
     :class-fn conn-status->class
     :label "Q-Link status"
     :sortable true}

    :wireless-rssi
    {:value-fn (comp :rssi :wireless)
     :render-fn (fn [{:keys [error average median min max count] :as _rssi} locator _props]
                  (or error
                      (when (nil? (get-in locator [:wireless :hub]))
                        (empty-cell-placeholder :wireless-rssi))
                      (s/join
                        "/"
                        [(gstring/format "%.1f" average)
                         median
                         min
                         max
                         count])))
     :sortingFn sorting-strings-case-insensitive
     :class-fn (fn [{:keys [error] :as _value} _props {:keys [wireless] :as _locator}]
                 (if error
                   "bg-danger"
                   (conn-status->class (:connection-status wireless))))
     :label "Q-Link connection RSSI (ave/median/min/max/count)"}

    :wireless-hub
    {:value-fn (comp :hub :wireless)
     :class-fn (fn [hub-name _props {:keys [connection-status] :as _locator}]
                 (when (some? hub-name)
                   (if (s/starts-with? hub-name "Aruba")
                     "bg-danger"
                     (if (= connection-status :Failed)
                       "bg-danger"
                       "bg-success"))))
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :wireless-hub)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :label    "Connected hub"
     :sortable true}

    :wireless-satellites
    {:value-fn (comp :slaves-count :wireless)
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :wireless-satellites)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :label "Connected satellites"
     :sortable true}))

(def locator-columns-network
  (l/map
    :ip-address
    {:value-fn :ip-address
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :ip-address)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :label    "IP address"}

    :ethernet-mac
    {:value-fn #(get-in % [:locator-info :ethernet-mac])
     :render-fn (fn [value]
                  (if (peinfo-utils/safe-empty? value)
                    (empty-cell-placeholder :ethernet-mac)
                    value))
     :sortingFn sorting-with-empty-placeholder-values
     :sortable true
     :label    "Ethernet mac"}))

(def all-columns
  (-> locator-columns-main
      (into locator-columns-hw-type-split)
      (into locator-columns-layout)
      (into locator-columns-radio)
      (into locator-columns-wireless)
      (into locator-columns-network)))

(def default-columns
  (into #{} (keys locator-columns-main)))

(reg-sub ::active-row?
  (fn [[_ _qpe-identity _locator-id]]
    (subscribe [:nav/match :parameters :query :locator-id]))
  (fn [active-locator-id [_ _qpe-identity locator-id]]
    (when active-locator-id
      (= active-locator-id locator-id))))

(reg-sub
  ::has-hw-type?
  (fn [[_ qpe-identity]]
    [(rf/subscribe [:current-project/all-locators qpe-identity])])
  (fn [[locators] _]
    (boolean (some :hw-type locators))))

(reg-sub
  ::any-locator-upgrading?
  (fn [[_ qpe-identity]]
    (rf/subscribe [:current-project/all-locators qpe-identity]))
  (fn [locators _]
    (boolean (some #(= :FWUpgrade (or (:connection-status %) (:command-status %))) locators))))

(rf/reg-sub
  ::user-preferences-locator-columns
  (fn [db [_ _]]
    (get-in db [:user-preferences :locator-columns])))

(rf/reg-sub
  ::user-preferences-locator-widths
  (fn [db [_ table-id]]
    (get-in db [:user-preferences :tables table-id :widths])))

(rf/reg-event-fx
  ::set-column-width
  (fn [{:keys [db]} [_ table-id col-id new-width]]
    (let [new-db (assoc-in db [:user-preferences :tables table-id :widths col-id] new-width)
          all-widths (get-in new-db [:user-preferences :tables table-id :widths])]
      {:db new-db
       :fx [[:update-local-storage {:local-storage-key "user-preferences"
                                    :value {:tables {table-id {:widths all-widths}}}}]]})))


(defn- evaluate-locator-column
  "Resolves class-fn, render-fn etc values for locator columns.
  Returns config with :value, :rendered, :class keys added and respective fn keys dissociated."
  [{:keys [value-fn render-fn class-fn]
    :or   {render-fn identity} :as config}
   locator
   props]
  (let [value (value-fn locator)]
    (-> config
        (assoc :value value
               :rendered (render-fn value locator props)
               :class (when class-fn
                        (class-fn value locator props)))
        (dissoc :class-fn :value-fn :render-fn))))

(reg-sub
  :current-project/all-locators
  (fn [[_ qpe-identity]]
    [(rf/subscribe [:qpe/data qpe-identity :project-state :runtime-locators])
     (rf/subscribe [:qpe/data qpe-identity :project-state :locators])
     (rf/subscribe [:qpe/data qpe-identity :current-project :cs-maps])])
  (fn [[runtime-locators project-locators-runtime-info project-cs] [_ _qpe-identity]]
    ;; Combine project locators
    ;; with runtime locators (seen on the network, not on the project)
    (concat
      (mapcat (fn [cs]
                (map (fn [static]
                       (-> static
                           ;; Merge the changing locator info to static locator info.
                           (merge (get project-locators-runtime-info (:id static)))
                           ;; Store CS name here, because not efficiently available on
                           ;; :value-fn otherwise (unless added in the backend)
                           (assoc ::coordinate-system-name (:name cs))))
                     (vals (:locator-maps cs))))
              (vals project-cs))
      (vec (vals runtime-locators)))))

(reg-sub
  ::locator-by-id
  (fn [[_ qpe-identity _search-string]]
    (rf/subscribe [:current-project/all-locators qpe-identity]))
  (fn [locators [_ _qpe-identity locator-id]]
    (first (filter #(= locator-id (:id %)) locators))))

;; Find different values for given locator property
;; {value uses}
;; Used for filters sidepanel and locator status shortcuts on the table header
(reg-sub
  ::available-filter-values
  (fn [[_ qpe-identity _]]
    [(subscribe [:current-project/all-locators qpe-identity])])
  (fn [[locators] [_ _qpe-identity k]]
    (frequencies (keep k locators))))

(rf/reg-event-db
  ::add-filter
  (fn [db [_ field filter-name on-off]]
    (if on-off
      (update-in db [:user-preferences :locator-filters field] (fnil conj #{}) filter-name)
      (update-in db [:user-preferences :locator-filters field] (fnil disj #{}) filter-name))))

(rf/reg-event-fx
  ::clear-filters
  (fn [{:keys [db]} [_ _]]
    (let [new-db (update db :user-preferences dissoc :locator-filters)]
      {:db new-db
       :fx [[:update-local-storage {:local-storage-key "user-preferences"
                                    :value {:locator-filters nil}}]]})))

(reg-sub
  ::all-filters-enabled
  (fn [db [_ _]]
    (get-in db [:user-preferences :locator-filters])))

(defn does-match-filters [locator filters]
  (every? (fn [[filter-name filter-values]]
            (if (seq filter-values)
              (contains? filter-values (get locator filter-name))
              true))
          filters))

(reg-sub
  ::filtered-locators
  (fn [[_ qpe-identity _search-string]]
    [(rf/subscribe [:current-project/all-locators qpe-identity])
     (rf/subscribe [::all-filters-enabled])])
  (fn [[locators filters] [_ _qpe-identity]]
    (if filters
      (filter #(does-match-filters % filters) locators)
      locators)))

(reg-sub
  ::sorted-locators
  (fn [[_ qpe-identity _sort-field _sort-order]]
    (rf/subscribe [::filtered-locators qpe-identity]))
  (fn [filtered-locators [_ _qpe-identity sort-field sort-order]]
    (let [column (get all-columns sort-field)]
      (cond-> (sort-by (:value-fn column) filtered-locators)
        (not sort-order) reverse))))

(defn sidebar-layout [props locator configuration]
  [:div.flex-grow-1
   {:style {:overflow-y "auto"
            :flex-basis "0"}}
   [:div
    (doall
      (for [[column config] configuration
            :let [{:keys [rendered label class]} (evaluate-locator-column config locator props)]]
        [:div.p-2.border-bottom
         {:key column}
         [:div.fw-bold label]
         [:div
          [:span.p-1
           {:class class}
           (or rendered "-")]]]))]])

(defn locator-details-content [props locator]
  (let [panel (-> props :parameters :query (:locator-panel "layout"))]
    [:<>
     [:ul.mt-2.px-2.nav.nav-tabs
      (doall
        (for [tab locator-sidebar-tabs]
          [:li.nav-item
           {:key (:id tab)}
           [:a.nav-link
            {:href "#"
             :on-click (fn [e]
                         (.preventDefault e)
                         (.stopPropagation e)
                         (rf/dispatch [:nav/update-query assoc :locator-panel (:id tab)]))
             :class    (when (= panel (:id tab))
                         "active")}
            (:label tab)]]))]
     (case panel
       "layout" [sidebar-layout props locator locator-columns-layout]
       "radio" [sidebar-layout props locator locator-columns-radio]
       "wireless" [sidebar-layout props locator locator-columns-wireless]
       "network" [sidebar-layout props locator locator-columns-network]
       nil)]))

(defn locator-details-tabs [{:keys [qpe-subscribe] :as props} locator-id]
  (let [locator @(qpe-subscribe [::locator-by-id locator-id])]
    (when locator
      [layout/sidebar
       [layout/sidebar-header
        {:title (or (:name locator)
                    locator-id)
         :on-close #(rf/dispatch [:nav/update-query dissoc :locator-id])}]
       ;; No sidebar-content, content is without padding.
       [locator-details-content props locator]])))

(defn filter-section
  "Reusable checkbox filter list for a single locator field.
   Required keys:
     :label               - section heading string
     :filter-key          - locator key to filter on (drives both the subscription and the dispatch)
     :qpe-subscribe       - contextual subscribe fn
     :all-filters-enabled - current filter state map from re-frame
   Optional keys:
     :render-fn           - fn to format each displayed value  (default: identity)
     :border?             - add border-bottom class to the wrapper (default: true)"
  [{:keys [label filter-key qpe-subscribe all-filters-enabled render-fn border?]
    :or   {render-fn identity border? true}}]
  [:div {:class (if border? "select-list border-bottom" "select-list")}
   [:h4 label]
   (for [[filter-name number-of-rows] @(qpe-subscribe [::available-filter-values filter-key])]
     [:label
      {:key           filter-name
       :on-mouse-down (fn [e] (.preventDefault e))}
      [:input {:type      "checkbox"
               :checked   (-> all-filters-enabled filter-key (contains? filter-name) boolean)
               :on-change #(rf/dispatch [::add-filter filter-key filter-name (.. % -target -checked)])}]
      [:span.pl-3 (render-fn filter-name)]
      [:span.pl-3 number-of-rows]])])

(defn locator-filtering [{:keys [qpe-subscribe qpe-dispatch]}]
  (let [all-filters-enabled @(rf/subscribe [::all-filters-enabled])
        has-hw-type?        @(qpe-subscribe [::has-hw-type?])]
    [layout/sidebar
     [:div.d-flex.flex-column.border-bottom
      [:div.sidebar-header
       [:h3.sidebar-header-title "Filter locators"]
       ;; On desktop, close button keeps the changes
       [button/close-button
        {:class "p-3 d-none d-md-block"
         :on-click #(rf/dispatch [::e/apply-panel-changes :locator-filters])}]
       ;; On mobile, close button cancels the changes
       [button/close-button
        {:class "p-3 d-block d-md-none"
         :on-click #(rf/dispatch [::e/cancel-panel-changes :locator-filters])}]]

      [:div.sidebar-header-tools
       [:label.d-flex.align-items-center.m-0
        [:input {:type      "checkbox"
                 ;; Filters setting is empty, or is a map with every value being empty set.
                 :checked   (boolean (or (empty? all-filters-enabled)
                                         (every? empty? (vals all-filters-enabled))))
                 :on-change #(rf/dispatch [::clear-filters])}]
        [:span.pl-3 "Show all"]]
       [:button.btn.btn-link.text-decoration-none
        {:on-click #(rf/dispatch [::clear-filters])}
        "Clear filters"]]]

     [:div.sidebar-content.sidebar-content--scroll
      [filter-section {:label               "Coordinate system"
                       :filter-key          ::coordinate-system-name
                       :qpe-subscribe       qpe-subscribe
                       :all-filters-enabled all-filters-enabled}]

      (if has-hw-type?
        ;; hw-type is present: show a single "Type" filter section
        [filter-section {:label               "Type"
                         :filter-key          :hw-type
                         :qpe-subscribe       qpe-subscribe
                         :all-filters-enabled all-filters-enabled}]

        ;; hw-type is absent/nil: show Detected Type + Planned Type filter sections
        [:<>
         [filter-section {:label               "Detected Type"
                          :filter-key          :hw-type-detected
                          :qpe-subscribe       qpe-subscribe
                          :all-filters-enabled all-filters-enabled}]
         [filter-section {:label               "Planned Type"
                          :filter-key          :hw-type-planned
                          :qpe-subscribe       qpe-subscribe
                          :all-filters-enabled all-filters-enabled}]])

      [filter-section {:label               "Connection"
                       :filter-key          :command-status
                       :qpe-subscribe       qpe-subscribe
                       :all-filters-enabled all-filters-enabled
                       :render-fn           command-status-name
                       :border?             false}]]

     [:div.sidebar-actions
      [:button.btn.btn-outline-primary.btn-lg
       {:on-click #(rf/dispatch [::e/cancel-panel-changes :locator-filters])}
       "Cancel"]
      [:button.btn.btn-primary.btn-lg.ml-3
       {:on-click #(rf/dispatch [::e/apply-panel-changes :locator-filters])}
       "Apply"]]]))

(defn sidebar [{:keys [parameters] :as props} effective-all-columns effective-default-columns]
  (or
    (when-let [locator-id (-> parameters :query :locator-id)]
      [locator-details-tabs props locator-id])
    (when-let [sidebar-type (-> parameters :query :panel)]
      (case sidebar-type
        :locator-filters
        [locator-filtering props]
        :locator-columns
        [tables/columns-sidebar props
         {:k               :locator-columns
          :default-columns effective-default-columns
          :all-columns     effective-all-columns}]
        nil))))

(defn update-firmware-button [{:keys [qpe-dispatch locator-ids update-locators-count disabled? show-tooltip? pending?]}]
  (let [button [:button.btn.btn-primary
                {:disabled (or disabled? pending?)
                 :on-click (fn []
                             (qpe-dispatch [:qpe/update-locator-fw locator-ids]))}
                (if pending?
                  "Firmware update running"
                  (str "Update firmware (" update-locators-count ")"))]]
    (if (and disabled? show-tooltip? (not pending?))
      [tooltip/overlay-trigger
       {:placement "top"
        :rootClose true
        :trigger "hover"
        :defaultShow false
        :overlay (r/as-element
                   [tooltip/tooltip
                    {:id "tooltip-update-firmware"}
                    "All locators are already up to date"])}
       [:span.d-inline-block {:tab-index 0} button]]
      button)))

(defn locator-grid
  [{:keys [qpe-subscribe qpe-dispatch parameters] :as props}]
  (let [sort-by-field       (-> parameters :query (:sort-field :id))
        sort-order-asc      (-> parameters :query (:sort-asc true))
        sorted-locators     @(qpe-subscribe [::sorted-locators sort-by-field sort-order-asc])
        has-hw-type?        @(qpe-subscribe [::has-hw-type?])
        fw-update-pending?    @(qpe-subscribe [:qpe/data :ui :fw-update-pending?])
        any-locator-upgrading? @(qpe-subscribe [::any-locator-upgrading?])
        upgrade-running?      (or fw-update-pending? any-locator-upgrading?)
        latest @(qpe-subscribe [:qpe/data :controller-state :firmware-versions :latestQxxMainFW])
        ;; When hw-type is present: show the combined :hw-type column, hide the split pair.
        ;; When hw-type is absent:  show :hw-type-detected / :hw-type-planned, hide :hw-type.
        effective-all-columns (if has-hw-type?
                                (dissoc all-columns :hw-type-detected :hw-type-planned)
                                (dissoc all-columns :hw-type))
        effective-default-columns (if has-hw-type?
                                    default-columns
                                    (-> default-columns
                                        (disj :hw-type)
                                        (conj :hw-type-detected :hw-type-planned)))
        all-filters-enabled @(rf/subscribe [::all-filters-enabled])
        connection-status-filters-available @(rf/subscribe [::available-filter-values :command-status])
        selected-columns    (or @(rf/subscribe [::user-preferences-locator-columns])
                                effective-default-columns)
        persisted-widths    @(rf/subscribe [::user-preferences-locator-widths :locators])
        warning-title " warning"
        alerts @(qpe-subscribe [:controller-ui/messages])
        locators-ids-with-too-old-firmware (->> sorted-locators
                                                (filter (fn [locator]
                                                          (-> locator :locator-info :fw-too-old?)))
                                                (map :id))
        old-firmware-locator-ids (->> sorted-locators
                                      (filter (fn [locator]
                                                (and (not= (-> locator :locator-info :main-fw-str) latest)
                                                     (not (nil? (-> locator :locator-info :main-fw-str))))))
                                      (map :id))
        old-firmware-locator-ids-count (count old-firmware-locator-ids)
        old-firmware-locator-button-disabled? (= old-firmware-locator-ids-count 0)
        too-old-fw-locator-count (count locators-ids-with-too-old-firmware)
        too-old-locator-firmware-alert (when (seq alerts)
                                         (->> alerts
                                            (filter (fn [alert]
                                                      (= (:id alert) :locators-too-old-firmware)))))
        too-old-update-button-firmware-disabled? (= too-old-fw-locator-count 0)]

    [layout/section
     [layout/view-content
      {:sidebar-open (or (:panel (:query parameters))
                         @(qpe-subscribe [:qpe/data :ui :sidebar :type]))
       :scroll-container false}
      [layout/view-header
       {:title "Locators"}]

      [alert-popover {:alerts too-old-locator-firmware-alert
                      :title warning-title
                      :on-close! (fn [alert-id]
                                   (qpe-dispatch [:controller-ui/close-message alert-id]))
                      :action (update-firmware-button
                                {:qpe-dispatch qpe-dispatch
                                 :locator-ids locators-ids-with-too-old-firmware
                                 :update-locators-count too-old-fw-locator-count
                                 :disabled? too-old-update-button-firmware-disabled?
                                 :pending? upgrade-running?})}]

      [:div.d-flex.flex-row.my-2
       (doall
         (for [[connection-state number-available] connection-status-filters-available
               :let [active (-> all-filters-enabled :command-status (contains? connection-state))]]
           [:button.btn.mr-2
            {:key      (name connection-state)
             :class    (if active "btn-outline-primary" "btn-outline-secondary")
             :on-click #(rf/dispatch [::add-filter :command-status connection-state (not active)])}
            (if active
              [icon :close.mr-2]
              [:div.qi-circle.mr-2
               {:class (case connection-state
                         :WaitingForStatus "text-warning"
                         :WaitingForReply "text-success"
                         :Ok "text-success"
                         "text-danger")}])

            (str number-available " " (command-status-name connection-state))]))]

      [:div.d-flex.flex-row.justify-content-between.py-2.align-items-center
       [:div
        [:span.align-middle.mr-4 "Showing " (count sorted-locators) " locators"]
        [:button.btn.btn-outline-secondary
         {:hidden    (nil? (some seq (vals all-filters-enabled)))
          :on-click (fn [_e]
                      (rf/dispatch [::clear-filters]))}

         "Clear filters"
         [icon :close.ml-2]]]
       [:div.btn-toolbar
        [:button.btn.btn-outline-primary
         {:on-click #(qpe-dispatch [::e/open-panel :locator-columns])}
         [icon :sliders.mr-3]
         "Columns"]
        [:button.btn.btn-outline-primary.mx-2
         {:on-click #(qpe-dispatch [::e/open-panel :locator-filters])}
         [icon :filter.mr-3]
         "Filter"]
        (update-firmware-button
          {:qpe-dispatch qpe-dispatch
           :locator-ids old-firmware-locator-ids
           :update-locators-count old-firmware-locator-ids-count
           :disabled? old-firmware-locator-button-disabled?
           :pending? upgrade-running?
           :show-tooltip? true})]]

      [:div.d-flex.flex-column.scroll-container
       {:style {:flex "1 1 0"
                :min-height 0}}
        [tanstack-table/virtualized-table
         {:rows sorted-locators
          :all-columns effective-all-columns
          :selected-columns selected-columns
         :row-height 32
         :active-row-id (:locator-id (:query parameters))
         :props {:qpe-subscribe qpe-subscribe
                 :qpe-dispatch qpe-dispatch}
         :widths persisted-widths
         :on-column-resize (fn [col-id new-width]
                             (rf/dispatch [::set-column-width :locators col-id new-width]))
         :on-row-click (fn [row-data]
                         (rf/dispatch [:nav/update-query
                                       (fn [v] (assoc v :locator-id (:id row-data)))]))}]]]
     [sidebar props effective-all-columns effective-default-columns]]))

(def query-params
  [:map
   [:locator-id {:optional true} :string]
   [:locator-panel {:optional true} (into [:enum] (map :id locator-sidebar-tabs))]])

(def table-query-params
  (mu/merge tables/query-params query-params))
